import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { useDashboardStats } from '@/hooks/useQueries';
import { useConversations } from '@/hooks/useMessages';
import { searchCustomers, searchSubscriptions } from '@/lib/search-api';
import {
  SubscriptionStatus,
  buildPaymentIssuesSearchParams,
  paymentIssuesTileCount,
} from '@ruta/shared';
import { usePaymentIssuesFilter } from '@/hooks/usePaymentIssuesFilter';
import { useImportForkVisibility } from '@/hooks/useImportForkVisibility';
import {
  WORK_QUEUE_STAGES,
  type WorkQueueStageId,
} from '@/components/dashboard/live-ops/workQueueStages';

import type { PipelineStage } from '@/components/dashboard/live-ops/LifecyclePipeline';

const PENDING_RATES_FILTER = { status: 'all', rateRequested: true } as const;
const PENDING_ACTIVATIONS_FILTER = { status: SubscriptionStatus.ACTIVE, isReviewed: 'false' } as const;

export interface PipelineStagesResult {
  stages: PipelineStage[];
  loading: boolean;
}

/**
 * Builds the operational work-queue stages (counts + click-through) for the
 * compact `MiniPipeline` in the top header. Payment-issue criteria come from
 * Preferences → Work queue (shared store). Every underlying source is React
 * Query, so multiple mounts dedupe to a single set of requests.
 */
export function usePipelineStages(): PipelineStagesResult {
  const navigate = useNavigate();
  const { showPayments } = useImportForkVisibility();

  const { data: stats, isLoading: statsLoading } = useDashboardStats();

  // User-chosen payment-issue criteria (Preferences → Work queue).
  const [paymentIssuesConfig] = usePaymentIssuesFilter();

  // The customers-page filter shape used both for the count and the click-through.
  const paymentIssuesFilter = useMemo(
    () => ({
      // Status is a single value on the customers page. Exactly one selected
      // narrows to it; both or none selected removes the constraint ('all').
      status: paymentIssuesConfig.statuses.length === 1 ? paymentIssuesConfig.statuses[0] : 'all',
      paymentStatus: paymentIssuesConfig.paymentStatus,
      ...(paymentIssuesConfig.hasBalance ? { balanceFilter: 'has_balance' } : {}),
    }),
    [paymentIssuesConfig],
  );

  // Actively-snoozed rate requests don't count against the pending metric
  // (RUT-2698); the click-through list still shows them, set apart by a
  // "Snoozed until" badge. Expired snoozes count again automatically.
  const { data: pendingRatesCount } = useQuery({
    queryKey: ['pipeline', 'pendingRates'],
    queryFn: () =>
      searchSubscriptions({ ...PENDING_RATES_FILTER, excludeSnoozed: true, per_page: 0 }).then((r) => r.found),
  });

  // Payment-issue count re-runs whenever the chosen criterion changes so the
  // badge stays in sync with the gear selection.
  const { data: paymentIssuesCount } = useQuery({
    queryKey: ['pipeline', 'paymentIssues', paymentIssuesConfig],
    queryFn: () =>
      searchCustomers({
        ...buildPaymentIssuesSearchParams(paymentIssuesConfig),
        per_page: 0,
      }).then((r) => r.found),
    enabled: showPayments,
  });

  // Unread customer conversations. The server returns an exact org-wide
  // `totalUnread` on every page, so we read that rather than counting rows.
  const { data: unreadData } = useConversations({ unreadOnly: true });
  const unreadMessagesCount = useMemo(() => {
    const page = unreadData?.pages?.[0];
    if (!page) return undefined;
    return page.totalUnread;
  }, [unreadData]);

  // Stage identity (order, icon, wording) comes from WORK_QUEUE_STAGES so the
  // header and Preferences → Work queue can never drift apart. This hook only
  // supplies what it owns: the live count and where each stage clicks through to.
  const stages: PipelineStage[] = useMemo(() => {
    const counts: Record<WorkQueueStageId, number | undefined> = {
      messages: unreadMessagesCount,
      rates: pendingRatesCount ?? stats?.pendingRateRequests,
      activations: stats?.subscriptionsNeedingReview,
      review: stats?.visitsRequiringReview,
      photos: stats?.photosRequiringReview,
      // Same `/search?type=customers` `found` the customers-page drill-in uses.
      // Never substitute dashboard `paymentIssues.total` (RUT-3985).
      payments: paymentIssuesTileCount(paymentIssuesCount),
    };

    const navigators: Record<WorkQueueStageId, () => void> = {
      messages: () => navigate('/messages?tab=customers&filter=unread'),
      rates: () =>
        navigate('/services?tab=config', {
          state: { clearFilters: true, filters: PENDING_RATES_FILTER },
        }),
      activations: () =>
        navigate('/services?tab=config', {
          state: { clearFilters: true, filters: PENDING_ACTIVATIONS_FILTER },
        }),
      review: () => navigate('/review'),
      photos: () => navigate('/review?tab=photos'),
      payments: () =>
        navigate('/customers', {
          state: { clearFilters: true, filters: paymentIssuesFilter },
        }),
    };

    return WORK_QUEUE_STAGES.filter((stage) => showPayments || stage.id !== 'payments').map((stage) => ({
      ...stage,
      count: counts[stage.id],
      navigate: navigators[stage.id],
    }));
  }, [
    stats,
    pendingRatesCount,
    paymentIssuesCount,
    unreadMessagesCount,
    navigate,
    paymentIssuesFilter,
    showPayments,
  ]);

  return { stages, loading: statsLoading };
}
